import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {useTranslation} from 'react-i18next';
import {useEffect, useRef, useState} from 'react';
import WaiverText from './WaiverText';
import {MobileDatePicker} from '@mui/x-date-pickers';
import dayjs from 'dayjs';

export default function AcceptWaiverStep({nextStep, previousStep, handleChange, workFlowData}) {
  const {t} = useTranslation();

  const genders = ['male', 'female', 'other'];

  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState(workFlowData.dateOfBirth);

  const firstNameFieldRef = useRef();
  const lastNameFieldRef = useRef();
  const genderFieldRef = useRef();
  const dateOfBirthFieldRef = useRef();

  useEffect(() => {
    setWaiverAccepted(workFlowData.waiverAccepted);
  }, [workFlowData]);

  function onSubmit(e) {
    e.preventDefault();
    // Manual date validation due to required inputs
    // not being handled property in MUI 5 and the MobileDatePicker
    if (!dateOfBirth) {
      dateOfBirthFieldRef.current.focus();
      return;
    }

    handleChange({
      waiverAccepted,
      firstName: firstNameFieldRef.current.value.trim(),
      lastName: lastNameFieldRef.current.value.trim(),
      dateOfBirth: dateOfBirth,
      gender: genderFieldRef.current.value,
    });
    nextStep();
  }

  function saveChangesAndGoBack() {
    handleChange({
      waiverAccepted,
      firstName: firstNameFieldRef.current.value,
      lastName: lastNameFieldRef.current.value,
      dateOfBirth: dateOfBirth,
      gender: genderFieldRef.current.value,
    });
    previousStep();
  }

  function updateWaiverAcceptedState(event) {
    setWaiverAccepted(event.target.checked);
  }

  return (
    <div>
      <Typography variant="h2" gutterBottom marked="center" align="center">
        {t('waiver-title')}
      </Typography>

      <Box component="form" onSubmit={onSubmit} sx={{mt: 6}}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextField
              inputRef={firstNameFieldRef}
              label={t('first-name')}
              autoFocus
              fullWidth
              margin="normal"
              name="PlayerFirstName"
              type="text"
              required
              size="large"
              defaultValue={workFlowData.firstName}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              inputRef={lastNameFieldRef}
              label={t('last-name')}
              fullWidth
              margin="normal"
              name="PlayerLastName"
              type="text"
              required
              size="large"
              defaultValue={workFlowData.lastName}
            />
          </Grid>
          <Grid item xs={6}>
            <MobileDatePicker
              label={t('date-of-birth')}
              name="DateOfBirth"
              minDate={dayjs('1925-12-01')}
              disableFuture
              size="large"
              inputRef={dateOfBirthFieldRef}
              value={dateOfBirth}
              onChange={setDateOfBirth}
              renderInput={(params) => <TextField
                  InputLabelProps={{shrink: true}}
                  fullWidth
                  required
                  margin="normal"
                  {...params } />}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl
              margin="normal"
              required
              size="large"
              fullWidth
            >
              <InputLabel
                label={t('gender.label')}
                name="GenderLabel"
                id="gender-label"
              >
                {t('gender.label')}
              </InputLabel>
              <Select
                labelId="gender-label"
                label={t('gender.label')}
                inputRef={genderFieldRef}
                name="GenderSelect"
                defaultValue={workFlowData.gender}
              >
                {
                  genders.map((gender, idx) =>
                    <MenuItem key={idx} value={gender}>{t(`gender.${gender}`)}</MenuItem>,
                  )
                }
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <WaiverText/>
          </Grid>
        </Grid>
        <FormControlLabel
          control={
            <Checkbox onChange={updateWaiverAcceptedState}
                      checked={waiverAccepted}
                      name="accept"
                      required
            />
          }
          label={t('accept-waiver')}
        />
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Button
              onClick={saveChangesAndGoBack}
              fullWidth
            >
              {t('back-button')}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              type="submit"
              fullWidth
            >
              {t('next-button')}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}
AcceptWaiverStep.propTypes = {
  nextStep: PropTypes.func.isRequired,
  previousStep: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  workFlowData: PropTypes.any.isRequired,
};
